// Reac Component
import React from 'react'
import { NavLink, Link, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';


// App Component
import { FamlaServices } from '../../services'
import { Metrics } from '../../constants'
import { Buttons, InputForm } from '../../components'
import { Routes } from '../../routes'

// First Login Handler
import { handleLoginSuccess } from '../../utils/loginResponseHandler'
import {
    peekCollectInviteReturn,
    clearCollectInviteReturnStorage,
    persistLoginRedirectQuery,
} from '../../utils/collectInviteRedirect'
import {
    clearAutomationRedirectStorage,
    navigateToAutomationRedirect,
    peekAutomationRedirect,
    persistAutomationRedirectQuery,
} from '../../utils/automationRedirect'
import { markAuthSyncGracePeriod, persistAuthTokens } from '../../utils/authUtils'
import {
    isStCorporateEmail,
    shouldRedirectToStPortal,
} from '../../utils/stCorporateRedirect'
import StRedirectNotice from './StRedirectNotice'
import { useMicrosoftFamlaSession } from '../../hooks/useMicrosoftFamlaSession'
import { config } from '../../services/config'
import TurnstileWidget from '../../components/auth/TurnstileWidget'

// Features
import { iAlert } from '../../interfaces'
import { trackUiButtonEventAsync, UI_BUTTON_EVENTS } from '../../services/api/uiButtonStatsService'
import ButtonSocialeNetwork from './ButtonSocialeNetwork'
import { useAppDispatch } from '../../stores'
import iSessions from './Sessions'
import { login } from '../../stores'
import { LoginButton } from '../../auth'
import './UsersLogin.css'


const isTurnstileRequiredResponse = (data: { status?: number; body?: any }) => {
    if (data?.status !== 403) return false;
    const errorCode = String(data.body?.data?.error_code ?? '');
    const responseType = String(data.body?.response_type ?? '');
    return (
        errorCode === 'TURNSTILE_REQUIRED' ||
        errorCode === 'TURNSTILE_INVALID' ||
        responseType === 'captcha_required' ||
        data.body?.data?.turnstile_required === true
    );
};


const UsersLogin = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [user, setUser] = React.useState({
        email: '',
        password: '',
        isSubmit: false,
        isValidMail: true
    });
    const [alert, setAlert] = React.useState<iAlert>({
        isActive: false,
        type: '',
        message: ''
    })
    /** Adresse corporate à orienter vers le portail SSO (affiche l'interstitiel). */
    const [stRedirectEmail, setStRedirectEmail] = React.useState<string | null>(null)
    const [showResendVerification, setShowResendVerification] = React.useState(false)
    const [resendLoading, setResendLoading] = React.useState(false)
    const [ssoError, setSsoError] = React.useState<string | null>(null)
    /** Affiché uniquement quand l'API exige un challenge (source de vérité serveur). */
    const [turnstileRequired, setTurnstileRequired] = React.useState(false)
    const [turnstileToken, setTurnstileToken] = React.useState<string | null>(null)
    const [turnstileResetSignal, setTurnstileResetSignal] = React.useState(0)
    const turnstileSiteKey = config.auth.turnstileSiteKey
    let [searchParams, setSearchParams] = useSearchParams()

    const resetTurnstileWidget = React.useCallback(() => {
        setTurnstileToken(null);
        setTurnstileResetSignal((n) => n + 1);
    }, []);
    
    // [GOOGLE SSO ERROR] Vérifier si une erreur Google SSO est présente dans l'URL
    React.useEffect(() => {
        const errorType = searchParams.get("error");
        const errorMessage = searchParams.get("message");
        if (errorType === "google_personal_email" && errorMessage) {
            setSsoError(decodeURIComponent(errorMessage));
            // Nettoyer l'URL
            setSearchParams({});
        }
    }, [searchParams, setSearchParams]);

    // [INVITATION TOKEN] Récupérer le process_id depuis sessionStorage (validé via token)
    // Le token a été validé dans AuthGuard/AuthViewComponent et les informations stockées dans sessionStorage
    let invitedProcessId = sessionStorage.getItem('invited_process_id');
    const invitationToken = sessionStorage.getItem('invitation_token');
    
    // [LOG] Vérifier la présence du token d'invitation
    if (invitedProcessId || invitationToken) {
        console.log('🔗 [UsersLogin] Invitation detected:', { invitedProcessId, hasToken: !!invitationToken });
    }

    React.useEffect(() => {
        persistLoginRedirectQuery(searchParams.get('redirect'))
        persistAutomationRedirectQuery(searchParams.get('redirect'))
    }, [searchParams])

    // [MICROSOFT SSO] Création session Famla après retour Microsoft réussi
    const { isLoading: microsoftSessionLoading } = useMicrosoftFamlaSession({
        redirectQueryParam: searchParams.get('redirect'),
        onStart: React.useCallback(() => {
            setAlert((currentAlert) => (
                currentAlert.isActive && currentAlert.type === 'danger'
                    ? { isActive: false, type: '', message: '' }
                    : currentAlert
            ));
        }, []),
        onError: React.useCallback(() => {
            setAlert({
                isActive: true,
                type: 'danger',
                message: t('views.auth.anErrorOcured')
            });
        }, [t]),
    });

    React.useEffect(() => {
        if (searchParams.get("status") != null) {
            if (searchParams.get("status") === "10") {
                setAlert({ isActive: true, type: 'warning', message: t('views.auth.yourSessionHasExp') });
            }

            setSearchParams({})
        }
    }, [searchParams, setSearchParams, t])

    React.useEffect(() => {
        const st = location.state as { fromInviteSignup?: boolean; email?: string } | null;
        if (!st?.fromInviteSignup) return;
        const prefillEmail = st.email;
        if (prefillEmail) setUser((u) => ({ ...u, email: prefillEmail }));
        setAlert({ isActive: true, type: 'info', message: t('views.auth.inviteExistingAccountUseLogin') });
        navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: {} });
    }, [location.pathname, location.search, location.state, navigate, t])


    const handleSubmit = async (event: React.SyntheticEvent) => {
        event.preventDefault();
        await trackUiButtonEventAsync(UI_BUTTON_EVENTS.auth.loginSubmit);

        if (user.email.length === 0) {
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.emailIsRequired') });
            return;
        }
        else if (user.password.length === 0) {
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.passwordIsRequired') });
            return;
        }

        // [ST REDIRECT] Compte corporate sur l'instance publique → interstitiel puis portail SSO,
        // avant toute tentative d'authentification.
        if (shouldRedirectToStPortal() && isStCorporateEmail(user.email)) {
            setStRedirectEmail(user.email.trim());
            return;
        }

        if (turnstileRequired && !turnstileToken) {
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.captchaMissing') });
            return;
        }

        const formData: { email: string; password: string; turnstile_token?: string } = {
            email: user.email,
            password: user.password,
        };
        if (turnstileRequired && turnstileToken) {
            formData.turnstile_token = turnstileToken;
        }

        setUser({ ...user, isSubmit: true });
        let data = await FamlaServices("api_host", "/auth/login", "POST", formData, "");

        // Token Turnstile one-time : toujours reset après une tentative
        if (turnstileRequired) {
            resetTurnstileWidget();
        }

        // Filet de sécurité : l'API refuse les adresses corporate sur l'instance publique.
        if (data.status === 403 && data.body?.data?.sso_portal_url) {
            setUser({ ...user, isSubmit: false });
            setStRedirectEmail(user.email.trim());
            return;
        }

        if (data.status === 200) {
            setShowResendVerification(false);
            setTurnstileRequired(false);
            setTurnstileToken(null);
            let sessions: iSessions = data.body.data.session;
            persistAuthTokens(data.body.data.access_token, data.body.data.refresh_token);
            markAuthSyncGracePeriod();
            dispatch(login({ token: data.body.data.access_token, session: sessions, user: data.body.data.session.user }));
            setUser({ email: '', password: '', isSubmit: false, isValidMail: true });
            
            // [PREMIÈRE CONNEXION] Traiter le flag is_first_login depuis l'API
            handleLoginSuccess(data.body);
            
            // [AUTOMATION SSO] Return to famla-automation after login
            const automationReturn = peekAutomationRedirect(searchParams.get('redirect'));
            if (sessions.company_id !== null && automationReturn) {
                navigateToAutomationRedirect(automationReturn);
                return;
            }

            // [COLLECT CONTRIBUTOR /r/:token] Retour après connexion (query ou sessionStorage pour OAuth)
            const collectReturn = peekCollectInviteReturn(searchParams.get('redirect'));
            if (sessions.company_id !== null && collectReturn) {
                clearCollectInviteReturnStorage();
                navigate(collectReturn, { replace: true });
                return;
            }

            // [VÉRIFICATION COMPANY] Si pas de company, rediriger vers création company (même si invité)
            if (sessions.company_id === null) {
                // Si invitation présente, elle sera préservée dans sessionStorage pour redirection après création
                console.log('🔗 [UsersLogin] No company, redirecting to create company. Invitation preserved:', { invitedProcessId, hasToken: !!invitationToken });
                navigate(Routes.CREATE_MY_COMPANY);
            } else if (invitedProcessId) {
                // [INVITATION + COMPANY] Si invitation présente et company existe, rediriger vers processus invité
                // NE PAS nettoyer sessionStorage ici - laisser AuthGuard le faire après redirection réussie
                navigate(`${Routes.PROCESS_INTERVIEW}${invitedProcessId}`, { replace: true });
            } else {
                // [PAS D'INVITATION + COMPANY] Redirection normale vers homepage
                navigate(Routes.HOMEVIEW);
            }

        }
        else if (isTurnstileRequiredResponse(data)) {
            const errorCode = String(data.body?.data?.error_code ?? '');
            setTurnstileRequired(true);
            setShowResendVerification(false);
            setAlert({
                isActive: true,
                type: 'warning',
                message: errorCode === 'TURNSTILE_INVALID'
                    ? t('views.auth.captchaInvalid')
                    : t('views.auth.captchaRequired'),
            });
            // Garder le mot de passe : l'utilisateur doit seulement valider le captcha puis renvoyer
            setUser({ ...user, email: user.email, password: user.password, isSubmit: false });
        }
        else if (data.status === 400) {
            setShowResendVerification(false);
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.incorrectEmailOrPassword') });
            setUser({ ...user, email: user.email, password: '', isSubmit: false });
        }
        else if (
            data.status === 403 &&
            data.body?.response_type === 'email_not_verified'
        ) {
            setShowResendVerification(true);
            setAlert({
                isActive: true,
                type: 'warning',
                message: `${t('views.auth.verifyYourEmail')}\n${t('views.auth.verifyYourEmailDetail')}`,
            });
            setUser({ ...user, email: user.email, password: '', isSubmit: false });
        }
        else if (data.status === 401) {
            const desc = String(data.body?.description ?? '').toLowerCase();
            const responseType = String(data.body?.response_type ?? '').toLowerCase();
            const isBanned =
                responseType.includes('banned') || desc.includes('banned');
            const legacyUnverified =
                !isBanned && desc.includes('verify') && desc.includes('email');
            setShowResendVerification(legacyUnverified);
            setAlert({
                isActive: true,
                type: legacyUnverified ? 'warning' : 'danger',
                message: isBanned
                    ? t('views.auth.thisEmailHasBanned')
                    : legacyUnverified
                      ? `${t('views.auth.verifyYourEmail')}\n${t('views.auth.verifyYourEmailDetail')}`
                      : t('views.auth.verifyYourEmail'),
            });
            setUser({ ...user, email: user.email, password: '', isSubmit: false });
        }
        else {
            setShowResendVerification(false);
            setUser({ ...user, email: user.email, password: '', isSubmit: false });
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.anErrorOcured') });
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { type, name, value, checked } = event.target;
        let fieldValue: string | number | boolean;
        if (type === 'checkbox') {
            fieldValue = checked;
        } else if (type === 'number') {
            fieldValue = value !== '' ? Number(value) : '';
        } else {
            fieldValue = value !== undefined ? value : '';
        }
        fieldValue = typeof fieldValue !== 'string' ? String(fieldValue) : fieldValue;
        const trimmedValue: string = typeof fieldValue === 'string' ? fieldValue.trim() : '';
        let isEmailValid = name === 'email' ? Metrics.mailFormat.test(trimmedValue) : true;

        setUser({ ...user, [name]: fieldValue, isValidMail: isEmailValid });
        if (name === 'email' || name === 'password') {
            setShowResendVerification(false);
        }
    };

    const handleResendVerification = async () => {
        const emailTrim = user.email.trim();
        if (!emailTrim || !Metrics.mailFormat.test(emailTrim)) {
            setAlert({ isActive: true, type: 'danger', message: t('views.auth.enterValidMail') });
            return;
        }
        await trackUiButtonEventAsync(UI_BUTTON_EVENTS.auth.loginResendVerification);
        setResendLoading(true);
        const data = await FamlaServices(
            'api_host',
            '/auth/resend-verification',
            'POST',
            { email: emailTrim },
            ''
        );
        setResendLoading(false);
        if (data.status === 200) {
            setAlert({
                isActive: true,
                type: 'success',
                message: t('views.auth.resendVerificationSuccess'),
            });
            setShowResendVerification(false);
        } else {
            const desc =
                typeof data.body?.description === 'string'
                    ? data.body.description
                    : t('views.auth.resendVerificationError');
            setAlert({ isActive: true, type: 'danger', message: desc });
        }
    };


    return (
        <div className="users-login-light">
            {stRedirectEmail && (
                <StRedirectNotice
                    email={stRedirectEmail}
                    onCancel={() => setStRedirectEmail(null)}
                />
            )}
            <div className="users-login-title-block">
                <h1 className="users-login-headline">{t('views.auth.signInToYourAccount')}</h1>
            </div>
            <form onSubmit={handleSubmit}>
                {alert.isActive && (
                    <div className={`alert alert-${alert.type}`} role="alert">
                        <div style={{ whiteSpace: 'pre-line' }}>
                            {alert.message}
                        </div>
                        {showResendVerification && (
                            <button
                                type="button"
                                className="btn btn-sm btn-theme mt-3"
                                disabled={resendLoading || user.email.trim().length === 0}
                                onClick={handleResendVerification}
                            >
                                {resendLoading ? t('views.auth.pleaseWait') : t('views.auth.resendVerificationLink')}
                            </button>
                        )}
                    </div>
                )}

                {/* [GOOGLE SSO ERROR] Afficher l'erreur si présente */}
                {ssoError && (
                    <div className="alert alert-warning mb-3" role="alert">
                        <i className="fa-solid fa-triangle-exclamation me-2"></i>
                        {t('views.auth.googleSsoProOnly') || ssoError}
                        <button 
                            type="button" 
                            className="btn-close float-end" 
                            onClick={() => setSsoError(null)}
                            aria-label="Close"
                        ></button>
                    </div>
                )}

                <LoginButton
                    label={t('views.auth.continueWithMicrosoft') || 'Continuer avec Microsoft'}
                    loadingLabel={t('views.auth.authenticating') || t('views.auth.pleaseWait') || 'Connexion en cours...'}
                    className="btn auth-ms-login-btn w-100 d-flex align-items-center justify-content-center"
                    spinnerClass="text-secondary"
                    isProcessing={microsoftSessionLoading}
                />
                <div className="mt-3">
                    <ButtonSocialeNetwork t={t} />
                </div>

                <div className="users-login-divider d-flex align-items-center">
                    <hr className="flex-grow-1" />
                    <span>{t('views.auth.or')}</span>
                    <hr className="flex-grow-1" />
                </div>

                <InputForm
                    inputType="email"
                    inputLabel={t('views.auth.emailAddresPro')}
                    inputName="email"
                    inputValue={user.email}
                    isFloatingLabel={true}
                    labelClass="text-theme"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
                    inputContainerClass={user.isValidMail ? 'mb-3' : 'mb-0'}
                />
                <span className={`text-danger fs-6 d-bloc ${!user.isValidMail ? 'mb-3' : 'mb-0'}`}>{user.isValidMail ? '' : t('views.auth.enterValidMail')}</span>

                <InputForm
                    inputType="password"
                    inputLabel={t('views.auth.password')}
                    inputName="password"
                    inputValue={user.password}
                    isFloatingLabel={true}
                    labelClass="text-theme"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
                    inputContainerClass="mb-3"
                    withIcon={true}
                    iconClass="text-theme"
                />
                <div className="users-login-forgot-row d-flex justify-content-between align-items-center">
                    <div>
                        <NavLink
                            to={Routes.AUTH_RESET_PASSWORD}
                            className="users-login-terracotta-link fs-6"
                            onClick={async (e) => {
                                e.preventDefault();
                                await trackUiButtonEventAsync(UI_BUTTON_EVENTS.auth.loginForgotPasswordLink);
                                navigate(Routes.AUTH_RESET_PASSWORD);
                            }}
                        >
                            {t('views.auth.forgotPassword')}
                        </NavLink>
                    </div>
                </div>

                {turnstileRequired && turnstileSiteKey && (
                    <TurnstileWidget
                        siteKey={turnstileSiteKey}
                        onToken={setTurnstileToken}
                        resetSignal={turnstileResetSignal}
                        className="users-login-turnstile"
                    />
                )}

                <Buttons
                    buttonType="submit"
                    name={t('views.auth.logIn')}
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => { handleSubmit(event) }}
                    isLoading={user.isSubmit}
                    btnClass="users-login-submit w-100"
                    labelClass="text-white fw-bold"
                    styleProps={{ minHeight: 48 }}
                    arrowVisible={false}
                    spinnerColor="text-white"
                    arrowColor=""
                    disabled={
                        user.password.length === 0 ||
                        user.email.length === 0 ||
                        (turnstileRequired && !turnstileToken)
                    }
                />
            </form>

            <p className="users-login-signup-footer mb-0">
                {t('views.auth.dontHaveAccount')}{' '}
                <Link
                    to={config.app.stOnlyMode ? '/st-portal' : Routes.AUTH_SIGNIN}
                    className="users-login-terracotta-link fw-medium"
                    onClick={async (e) => {
                        e.preventDefault();
                        await trackUiButtonEventAsync(UI_BUTTON_EVENTS.auth.loginSignupLink);
                        navigate(config.app.stOnlyMode ? '/st-portal' : Routes.AUTH_SIGNIN);
                    }}
                >
                    {t('views.auth.signUp')}
                </Link>
            </p>
        </div>
    )
}

export default UsersLogin;